import React, { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { compose } from "lib/apolloClient";
import { useDispatch } from "Hooks/useDispatch";
import { useI18n } from "Hooks";
import _ from "lodash";
import { useAIChatContext } from "UIComponents/AIChat/context/AIChatContext";

// components
import withPusherBind from "UIComponents/PusherConsumerHoc";

// design system
import { AI_TUTOR_LEARNING_TOOLS } from "@toddle-edu/common-utils";

// modules and utils
import {
  isLinkUnprocessed,
  getAITutorToolNamesAndChoices,
} from "Utils/AIUtils";
import { updateAttachment } from "OrganizationResources/modules/OrganizationResourcesModule";
import { updateAiMessage } from "UIComponents/AIChat/modules/AIChatModules";

// scss
import classes from "./ProgressIndicator.scss";

/* -------------------- CONSTANTS -------------------- */

const EVENT_TEXT_MAPPING = {
  pending: "ai:analysing_the_video",
  fetchingTranscript: "ai:preparing_captions_and_visual_notes",
  preparingEnhancedTranscript: "ai:preparing_captions_and_visual_notes",
  uploadingEnhancedTranscript: "ai:preparing_captions_and_visual_notes",
  completed: "ai:preparing_captions_and_visual_notes",
  failed: "ai:analysing_the_video",
};

// per-attachment stage weights
const STAGE_PROGRESS = {
  fetchingTranscript: 0.33,
  preparingEnhancedTranscript: 0.66,
  uploadingEnhancedTranscript: 0.9,
  completed: 1,
  failed: 1,
};

/* -------------------- UI COMPONENT -------------------- */

const ProgressIndicator = ({ event = "pending", progress = 0 }) => {
  const [displayedProgress, setDisplayedProgress] = useState(0);
  const intervalRef = useRef(null);

  const { t } = useI18n();

  useEffect(() => {
    clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      setDisplayedProgress(prev => {
        if (prev >= progress) return prev;
        return Math.min(prev + 0.6, progress);
      });
    }, 40);

    return () => clearInterval(intervalRef.current);
  }, [progress]);

  return (
    <div className={classes.container}>
      <div className={classes.containerTop}>
        <span className={classNames("text-label", classes.textPrimary)}>
          {t(EVENT_TEXT_MAPPING[event])}
        </span>
        <span className={classNames("text-body-s", classes.textSecondary)}>
          {t("ai:processing_video_subtext")}
        </span>
      </div>

      <div className={classes.progressTrack}>
        <div
          className={classes.progressFill}
          style={{ width: `${displayedProgress}%` }}
        />
      </div>
    </div>
  );
};

const ComposedProgressIndicator = compose(
  withPusherBind([
    "failed",
    "fetchingTranscript",
    "preparingEnhancedTranscript",
    "uploadingEnhancedTranscript",
    "completed",
  ])
)(ProgressIndicator);

/* -------------------- WRAPPER -------------------- */

const ProgressIndicatorWrapper = props => {
  const { userMessage, unprocessedLinks = [], onSendMessage } = props;

  const [event, setEvent] = useState("pending");
  const [progress, setProgress] = useState(
    userMessage?.metadata?.transcriptionProgress || 5
  );

  const resolvedAttachmentsRef = useRef(new Set());
  const attachmentStageRef = useRef({});
  const hasTriggeredSendRef = useRef(false);

  const { setIsAIGeneratingResponse } = useAIChatContext();
  const dispatch = useDispatch();

  const attachmentIds = _.map(unprocessedLinks, "id");

  const sendMessageOnce = useCallback(async () => {
    if (hasTriggeredSendRef.current) return;
    hasTriggeredSendRef.current = true;

    const { id, content = "", attachments, metadata } = userMessage || {};
    const learningTool = metadata?.learningTool || {};

    const modalityTool = _.find(AI_TUTOR_LEARNING_TOOLS, {
      key: learningTool,
    });
    const toolName = modalityTool?.toolName;

    const { toolNames, toolChoice } = getAITutorToolNamesAndChoices({
      selectedLearningTool: { toolName },
    });

    await onSendMessage?.({
      content,
      attachments,
      toolNames,
      toolChoice,
      sendButtonParams: {
        createUserMessage: false,
        userMessageId: id,
      },
      learningTool: modalityTool?.key,
    });

    setIsAIGeneratingResponse?.(false);
  }, [userMessage, onSendMessage, setIsAIGeneratingResponse]);

  useEffect(() => {
    if (_.some(unprocessedLinks, link => !isLinkUnprocessed(link))) {
      sendMessageOnce();
    }
  }, []);

  const updateProgress = async () => {
    const total = _.size(attachmentIds);
    if (!total) return;

    let sum = 0;
    _.forEach(attachmentIds, id => {
      sum += attachmentStageRef.current[id] || 0;
    });

    const nextProgress = (sum / total) * 100;
    setProgress(nextProgress);

    if (!_.isEmpty(userMessage)) {
      await dispatch(
        updateAiMessage({
          id: userMessage.id,
          metadata: {
            ...(userMessage?.metadata || {}),
            transcriptionProgress: nextProgress,
          },
        })
      );
    }
  };

  const tryTriggerSend = async () => {
    const total = _.size(unprocessedLinks);
    if (resolvedAttachmentsRef.current.size !== total) return;
    await sendMessageOnce();
  };

  const onPusherEventTriggered = useCallback(
    async ({ event, attachmentId }) => {
      if (!_.includes(attachmentIds, attachmentId)) return;

      setEvent(event);

      // stage-based progress update
      if (STAGE_PROGRESS[event] !== undefined) {
        attachmentStageRef.current[attachmentId] = Math.max(
          attachmentStageRef.current[attachmentId] || 0,
          STAGE_PROGRESS[event]
        );
        updateProgress();
      }

      if (event === "completed") {
        resolvedAttachmentsRef.current.add(attachmentId);
        await tryTriggerSend();
      }

      if (event === "failed") {
        resolvedAttachmentsRef.current.add(attachmentId);

        await dispatch(
          updateAttachment({
            id: attachmentId,
            metadata: { isDeleted: true },
          })
        );

        await tryTriggerSend();
      }
    },
    [attachmentIds, dispatch]
  );

  return (
    <ComposedProgressIndicator
      {...props}
      event={event}
      progress={progress}
      onPusherEventTriggered={onPusherEventTriggered}
    />
  );
};

export default ProgressIndicatorWrapper;
